import React, { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Ban, Trash2, RotateCcw, Pencil, Check, X } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { formatINR } from "./shared";

export default function UsersPanel({ users, onChange }) {
  const [busy, setBusy] = useState("");
  const [reconcileId, setReconcileId] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [editRef, setEditRef] = useState(null);
  const [refVal, setRefVal] = useState("");
  const [savingRef, setSavingRef] = useState(false);

  const saveReferral = async (u) => {
    setSavingRef(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/referral`, { referral_code: refVal.trim() });
      if (!data.referral_code) toast.success(`Referral removed for ${u.name}`);
      else if (data.valid) toast.success(data.free
        ? `Applied ${data.referral_code} — ${u.name} now pays FREE at checkout`
        : `Applied ${data.referral_code} — ${u.name} now pays ${formatINR(data.final_price)} (₹${data.savings} off)`);
      else toast.warning(`Saved ${data.referral_code}, but it doesn't resolve to a discount — full price ${formatINR(data.base_price)} applies (ambassador still credited).`);
      setEditRef(null);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSavingRef(false); }
  };

  const reconcilePayment = async () => {
    const pid = reconcileId.trim();
    if (!pid) { toast.error("Enter a Razorpay payment ID (pay_XXXX)"); return; }
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/reconcile-payment", { payment_id: pid });
      if (data.already_paid) toast.success(`${data.user_name} was already marked paid.`);
      else if (data.reconciled) toast.success(`Reconciled ₹${data.amount} for ${data.user_name} (${data.user_email}). They can now log in; receipt emailed.`);
      else toast.message("Nothing changed.");
      setReconcileId("");
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setReconciling(false); }
  };

  const toggleTester = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/tester`, { enabled: !u.is_tester });
      toast.success(!u.is_tester
        ? `${u.name} can now pay in TEST mode (test user)`
        : `${u.name} removed as test user`);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const toggleDisqualify = async (u) => {
    if (!u.disqualified && !window.confirm(`Disqualify ${u.name}? They will be blocked from logging in.`)) return;
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/disqualify`, { enabled: !u.disqualified });
      toast.success(!u.disqualified ? `${u.name} disqualified` : `${u.name} reinstated`);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently DELETE ${u.name} (${u.college_email})?\n\nThis removes their account, registration, team membership, invites and certificates. If they lead a team, it will be disbanded. This cannot be undone.`)) return;
    setBusy(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted${data.disbanded_team ? " (their team was disbanded)" : ""}`);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const refundPayment = async (u) => {
    if (u.payment_status !== "paid" || !u.payment_id) return;
    const amtStr = window.prompt(`Refund ${u.name}'s payment (${u.payment_id}).\n\nLeave blank for a FULL refund, or enter an amount in ₹ for a partial refund:`, "");
    if (amtStr === null) return;
    const amount = amtStr.trim() ? Number(amtStr.trim()) : undefined;
    if (amtStr.trim() && (isNaN(amount) || amount <= 0)) { toast.error("Enter a valid amount"); return; }
    if (!window.confirm(`Confirm refund of ${amount ? "₹" + amount : "the FULL amount"} to ${u.name}? This is processed via Razorpay and cannot be undone.`)) return;
    setBusy(u.id);
    try {
      const { data } = await api.post(`/admin/payments/${u.payment_id}/refund`, amount ? { amount } : {});
      toast.success(data.mock ? "Test payment marked refunded" : `Refunded ₹${data.amount} (${data.refund_status || "processing"})`);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4">
      <div className="card-surface" data-testid="reconcile-payment-card">
        <div className="flex items-center gap-2 mb-1">
          <RotateCcw className="w-4 h-4 text-brand" />
          <h3 className="text-white font-semibold">Reconcile a captured payment</h3>
        </div>
        <p className="text-sm text-gray-400 mb-3">
          If Razorpay shows a payment as <span className="text-white">Captured</span> but the participant isn&apos;t marked paid (they couldn&apos;t get in), paste the Razorpay payment ID below. We&apos;ll fetch it, confirm it&apos;s captured, mark them paid and email the receipt.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={reconcileId}
            onChange={(e) => setReconcileId(e.target.value)}
            placeholder="pay_XXXXXXXXXXXXXX"
            className="input-field flex-1 font-mono"
            data-testid="reconcile-payment-input"
          />
          <button
            onClick={reconcilePayment}
            disabled={reconciling || !reconcileId.trim()}
            className="btn-primary min-h-[48px] flex items-center justify-center gap-2 whitespace-nowrap"
            data-testid="reconcile-payment-btn"
          >
            {reconciling ? <span className="spinner" /> : <RotateCcw className="w-4 h-4" />} Reconcile &amp; grant access
          </button>
        </div>
      </div>

      <div className="card-surface overflow-x-auto" data-testid="users-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="label-mono text-left border-b border-white/10">
            <th className="py-3 pr-4">Name</th>
            <th className="py-3 pr-4">Emails</th>
            <th className="py-3 pr-4">College</th>
            <th className="py-3 pr-4">Verified</th>
            <th className="py-3 pr-4">Payment</th>
            <th className="py-3 pr-4">Amount</th>
            <th className="py-3 pr-4">Payment Ref</th>
            <th className="py-3 pr-4">Referral</th>
            <th className="py-3 pr-4">Test User</th>
            <th className="py-3 pr-4">Moderation</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
              <td className="py-3 pr-4 text-white">
                {u.name}
                {u.is_tester && <span className="badge badge-invited ml-2 inline-flex items-center gap-1" data-testid={`user-tester-badge-${u.id}`}><FlaskConical className="w-3 h-3" /> tester</span>}
                {u.disqualified && <span className="badge badge-rejected ml-2 inline-flex items-center gap-1" data-testid={`user-dq-badge-${u.id}`}><Ban className="w-3 h-3" /> disqualified</span>}
              </td>
              <td className="py-3 pr-4 text-gray-400">
                <div data-testid={`user-college-email-${u.id}`}>{u.college_email}</div>
                {u.personal_email && u.personal_email !== u.college_email && (
                  <div className="text-xs text-gray-600 mt-0.5" data-testid={`user-personal-email-${u.id}`}>
                    <span className="text-gray-700">personal:</span> {u.personal_email}
                  </div>
                )}
              </td>
              <td className="py-3 pr-4 text-gray-400">{u.college_name}</td>
              <td className="py-3 pr-4">{u.college_verified ? <span className="badge badge-accepted">yes</span> : <span className="badge badge-invited">no</span>}</td>
              <td className="py-3 pr-4">{u.payment_status === "paid" ? <span className="badge badge-accepted">paid</span> : <span className="badge badge-invited">pending</span>}</td>
              <td className="py-3 pr-4 text-white">{u.amount_paid ? formatINR(u.amount_paid) : "—"}</td>
              <td className="py-3 pr-4">
                {u.payment_id ? (
                  <div data-testid={`user-payment-ref-${u.id}`}>
                    <button
                      onClick={() => { navigator.clipboard.writeText(u.payment_id).then(() => toast.success("Payment ref copied")).catch(() => {}); }}
                      className="font-mono text-xs text-gray-200 hover:text-white underline decoration-dotted underline-offset-2"
                      title={`${u.payment_id} (click to copy)`}>
                      {u.payment_id.length > 18 ? `${u.payment_id.slice(0, 18)}…` : u.payment_id}
                    </button>
                    <div className="flex items-center gap-1 mt-0.5">
                      {u.payment_gateway && <span className="badge badge-upcoming text-[10px] uppercase">{u.payment_gateway}</span>}
                      {u.payment_mode === "test" && <span className="badge badge-invited text-[10px] uppercase">test</span>}
                      {(u.payment_status === "refunded" || u.payment_status === "partially_refunded") && <span className="badge badge-rejected text-[10px] uppercase" data-testid={`user-refunded-${u.id}`}>refunded</span>}
                    </div>
                    {u.payment_status === "paid" && (
                      <button onClick={() => refundPayment(u)} disabled={busy === u.id}
                        className="mt-1 text-[11px] font-mono uppercase tracking-wide text-gray-400 hover:text-[#EF4444] flex items-center gap-1"
                        data-testid={`user-refund-${u.id}`}>
                        <RotateCcw className="w-3 h-3" /> Refund
                      </button>
                    )}
                  </div>
                ) : <span className="text-gray-600" data-testid={`user-payment-ref-${u.id}`}>—</span>}
              </td>
              <td className="py-3 pr-4">
                {u.payment_status === "paid" ? (
                  u.referral_code
                    ? <span className="badge badge-accepted font-mono" data-testid={`user-referral-${u.id}`}>{u.referral_code}</span>
                    : <span className="text-gray-600" data-testid={`user-referral-${u.id}`}>—</span>
                ) : editRef === u.id ? (
                  <div className="flex items-center gap-1">
                    <input value={refVal} onChange={(e) => setRefVal(e.target.value)} placeholder="NSA-…"
                      onKeyDown={(e) => { if (e.key === "Enter") saveReferral(u); if (e.key === "Escape") setEditRef(null); }}
                      className="input-field !py-1 !px-2 text-xs font-mono w-28" autoFocus
                      data-testid={`user-referral-input-${u.id}`} />
                    <button onClick={() => saveReferral(u)} disabled={savingRef}
                      className="p-1 text-emerald-400 hover:text-emerald-300" title="Save"
                      data-testid={`user-referral-save-${u.id}`}>
                      {savingRef ? <span className="spinner" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setEditRef(null)} className="p-1 text-gray-500 hover:text-white" title="Cancel"
                      data-testid={`user-referral-cancel-${u.id}`}><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {u.referral_code
                      ? <span className="badge badge-accepted font-mono" data-testid={`user-referral-${u.id}`}>{u.referral_code}</span>
                      : <span className="text-gray-600" data-testid={`user-referral-${u.id}`}>—</span>}
                    <button onClick={() => { setEditRef(u.id); setRefVal(u.referral_code || ""); }}
                      className="p-1 text-gray-500 hover:text-brand" title="Edit referral code"
                      data-testid={`user-referral-edit-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </td>
              <td className="py-3 pr-4">
                <button
                  onClick={() => toggleTester(u)}
                  disabled={busy === u.id}
                  className={`!py-1 !px-2.5 text-xs ${u.is_tester ? "btn-secondary" : "btn-primary"}`}
                  data-testid={`user-tester-toggle-${u.id}`}>
                  {busy === u.id ? <span className="spinner" /> : (u.is_tester ? "Remove" : "Make tester")}
                </button>
              </td>
              <td className="py-3 pr-4">
                <button
                  onClick={() => toggleDisqualify(u)}
                  disabled={busy === u.id}
                  className={`!py-1 !px-2.5 text-xs ${u.disqualified ? "btn-secondary" : "btn-danger"}`}
                  data-testid={`user-disqualify-toggle-${u.id}`}>
                  {busy === u.id ? <span className="spinner" /> : (u.disqualified ? "Reinstate" : "Disqualify")}
                </button>
                <button
                  onClick={() => deleteUser(u)}
                  disabled={busy === u.id}
                  className="btn-danger !py-1 !px-2.5 text-xs ml-2 inline-flex items-center gap-1"
                  data-testid={`user-delete-${u.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={9} className="text-center text-gray-500 py-8">No users yet.</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}
